import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import { getMaps, getMapData } from '../services/api';
import { useGlobalContext } from '../contexts/GlobalContext';

const mainListItems = [
  { text: 'Dashboard', icon: <HomeRoundedIcon />, path: '/dashboard' },
  { text: 'Media', icon: <PermMediaIcon />, path: '/media' },
  {
    text: 'Monitor Groups',
    icon: <CategoryRoundedIcon />,
    path: '/monitor-groups',
  },
  {
    text: 'Entity Models',
    icon: <CategoryRoundedIcon />,
    path: '/entity-models',
  },
];

const secondaryListItems = [
  { text: 'Settings', icon: <SettingsRoundedIcon />, path: '/profile' },
];

export default function DeviceMappingMenuContent({
  collapsed = false,
  onNavigate,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setPendingPinHighlight } = useGlobalContext();
  const [maps, setMaps] = React.useState([]);
  const [loadingMaps, setLoadingMaps] = React.useState(false);

  // Collapsible state for individual maps (to show pins)
  const [expandedMaps, setExpandedMaps] = React.useState({});

  // Cached pins data per map
  const [mapPinsData, setMapPinsData] = React.useState({});
  const [loadingPins, setLoadingPins] = React.useState({});

  // Extract current map ID from route if on a map page
  const currentMapId = React.useMemo(() => {
    const match = location.pathname.match(/^\/maps\/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }, [location.pathname]);

  // Load maps on component mount
  React.useEffect(() => {
    const loadMaps = async () => {
      setLoadingMaps(true);
      try {
        const mapsData = await getMaps();
        setMaps(mapsData?.results || []);
      } catch (error) {
        console.error('Error loading maps:', error);
      } finally {
        setLoadingMaps(false);
      }
    };

    loadMaps();
  }, []);

  // Track which map IDs we've already auto-expanded to prevent redundant work
  const autoExpandedRef = React.useRef(new Set());

  // Auto-expand the current map when navigating to a map page
  React.useEffect(() => {
    if (currentMapId && maps.length > 0) {
      // Check if the map exists in our maps list
      const mapExists = maps.some(m => m.id === currentMapId);
      if (mapExists) {
        // Always ensure current map is expanded (using functional update to get latest state)
        setExpandedMaps(prev => {
          if (prev[currentMapId]) {
            return prev; // Already expanded, no change needed
          }
          return {
            ...prev,
            [currentMapId]: true,
          };
        });

        // Load pins if not already loaded (check ref to avoid duplicate requests)
        if (
          !autoExpandedRef.current.has(currentMapId) &&
          !mapPinsData[currentMapId]
        ) {
          autoExpandedRef.current.add(currentMapId);
          setLoadingPins(prev => ({ ...prev, [currentMapId]: true }));
          getMapData(currentMapId)
            .then(pinsData => {
              setMapPinsData(prev => ({
                ...prev,
                [currentMapId]: pinsData?.results || [],
              }));
            })
            .catch(error => {
              console.error('Error loading pins for map:', currentMapId, error);
              autoExpandedRef.current.delete(currentMapId); // Allow retry on error
            })
            .finally(() => {
              setLoadingPins(prev => ({ ...prev, [currentMapId]: false }));
            });
        }
      }
    }
  }, [currentMapId, maps, mapPinsData]);

  const handleNavigation = path => {
    navigate(path);
    onNavigate?.();
  };

  const handleToggleMapExpand = async (e, mapId) => {
    const isCurrentlyExpanded = expandedMaps[mapId];

    setExpandedMaps(prev => ({
      ...prev,
      [mapId]: !isCurrentlyExpanded,
    }));

    // Lazy load pins if expanding and not already loaded
    if (!isCurrentlyExpanded && !mapPinsData[mapId]) {
      setLoadingPins(prev => ({ ...prev, [mapId]: true }));
      try {
        const pinsData = await getMapData(mapId);
        setMapPinsData(prev => ({
          ...prev,
          [mapId]: pinsData?.results || [],
        }));
      } catch (error) {
        console.error('Error loading pins for map:', mapId, error);
      } finally {
        setLoadingPins(prev => ({ ...prev, [mapId]: false }));
      }
    }
  };

  const handlePinClick = (mapId, pin) => {
    // Set the pin data in global context so MapViewer can highlight it directly
    setPendingPinHighlight({
      mapId,
      pinId: pin.id,
      pinData: pin,
    });
    navigate(`/maps/${mapId}`);
    onNavigate?.();
  };

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <List dense>
        {mainListItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ display: 'block' }}>
            <Tooltip title={collapsed ? item.text : ''} placement="right" arrow>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}

        {/* Maps section header */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title={collapsed ? 'Maps' : ''} placement="right" arrow>
            <ListItemButton
              selected={location.pathname === '/maps'}
              onClick={() => handleNavigation('/maps')}
              sx={{
                minHeight: 48,
                justifyContent: collapsed ? 'center' : 'initial',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 0 : 3,
                  justifyContent: 'center',
                }}
              >
                <MapRoundedIcon />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Maps" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Maps list - always visible when sidebar is expanded */}
        {!collapsed && (
          <List component="div" disablePadding dense>
            {loadingMaps ? (
              <ListItem sx={{ pl: 4 }}>
                <ListItemText primary="Loading..." />
              </ListItem>
            ) : maps.length === 0 ? (
              <ListItem sx={{ pl: 4 }}>
                <ListItemText primary="No maps available" />
              </ListItem>
            ) : (
              maps.map(map => (
                <React.Fragment key={map.id}>
                  {/* Individual map - click name to navigate, click arrow to expand/collapse */}
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={e => handleToggleMapExpand(e, map.id)}
                        sx={{ mr: -1 }}
                      >
                        {expandedMaps[map.id] ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      sx={{ pl: 4, pr: 5 }}
                      selected={location.pathname === `/maps/${map.id}`}
                      onClick={() => {
                        navigate(`/maps/${map.id}`);
                        onNavigate?.();
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                      </ListItemIcon>
                      <ListItemText primary={map.name} />
                    </ListItemButton>
                  </ListItem>

                  {/* Collapsible pins list for this map */}
                  <Collapse
                    in={expandedMaps[map.id]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding dense>
                      {loadingPins[map.id] ? (
                        <ListItem sx={{ pl: 6 }}>
                          <ListItemText
                            primary="Loading pins..."
                            primaryTypographyProps={{ fontSize: '0.85rem' }}
                          />
                        </ListItem>
                      ) : !mapPinsData[map.id] ||
                        mapPinsData[map.id].length === 0 ? (
                        <ListItem sx={{ pl: 6 }}>
                          <ListItemText
                            primary="No pins"
                            primaryTypographyProps={{
                              fontSize: '0.85rem',
                              color: 'text.secondary',
                            }}
                          />
                        </ListItem>
                      ) : (
                        mapPinsData[map.id].map(pin => (
                          <ListItem key={pin.id} disablePadding>
                            <ListItemButton
                              sx={{ pl: 6, py: 0.5 }}
                              onClick={() => handlePinClick(map.id, pin)}
                            >
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                {pin.custom_pin_icon?.icon_image_url ? (
                                  <Box
                                    component="img"
                                    src={pin.custom_pin_icon.icon_image_url}
                                    alt={pin.name}
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      objectFit: 'contain',
                                    }}
                                  />
                                ) : (
                                  <PlaceIcon
                                    sx={{ fontSize: 16, color: 'primary.main' }}
                                  />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={pin.name}
                                primaryTypographyProps={{
                                  fontSize: '0.85rem',
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Collapse>
                </React.Fragment>
              ))
            )}
          </List>
        )}
      </List>
      <List dense>
        {secondaryListItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ display: 'block' }}>
            <Tooltip title={collapsed ? item.text : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}
